/**
 * Predefined layers for grouping course elements. Each layer can be shown/hidden.
 */

import type { SurfaceKey, ToolId } from './types';

export const LAYER_IDS = ['tee', 'green', 'playingArea', 'hazards'] as const;
export type LayerId = (typeof LAYER_IDS)[number];

/** Order of layer menu items (below hole list). */
export const LAYER_MENU_ORDER: readonly LayerId[] = ['playingArea', 'tee', 'hazards', 'green'];

/** Order of layer groups in the sidebar: Green, Playing Area, Tee, Hazards. */
export const SIDEBAR_LAYER_ORDER: readonly LayerId[] = ['green', 'playingArea', 'tee', 'hazards'];

/** Tools that belong to each layer (for grouping controls). */
export const TOOLS_BY_LAYER: Record<LayerId, readonly ToolId[]> = {
  playingArea: ['fairway', 'firstCut', 'secondCut', 'cartPath', 'marker150'],
  tee: ['tee', 'teePosition'],
  hazards: ['outOfBounds', 'water', 'bunker', 'waste', 'hazard', 'tree', 'house'],
  green: ['green', 'pin'],
};

/** Tools not in a layer (Pointer, Elevation, Eraser appear as icons in the layer bar). */
export const OTHER_TOOLS: readonly ToolId[] = [];

export function getToolsForLayer(layerId: LayerId): readonly ToolId[] {
  return TOOLS_BY_LAYER[layerId] ?? [];
}

export function getLayerForTool(toolId: ToolId): LayerId | 'other' {
  for (const layerId of LAYER_MENU_ORDER) {
    if ((TOOLS_BY_LAYER[layerId] as readonly string[]).includes(toolId)) return layerId;
  }
  return 'other';
}

/** Feature keys: non-surface data (tees, pins, trees, etc.) */
export type LayerFeatureKey = 'tees' | 'teePositions' | 'pins' | 'markers150' | 'trees' | 'houses';

export interface LayerConfig {
  id: LayerId;
  label: string;
  /** Surface keys in this layer (e.g. fairway, green). */
  surfaceKeys: SurfaceKey[];
  /** Non-surface features in this layer. */
  features: LayerFeatureKey[];
}

export const LAYER_CONFIG: LayerConfig[] = [
  {
    id: 'tee',
    label: 'Tee',
    surfaceKeys: [],
    features: ['tees', 'teePositions'],
  },
  {
    id: 'green',
    label: 'Green',
    surfaceKeys: ['green'],
    features: ['pins'],
  },
  {
    id: 'playingArea',
    label: 'Playing area',
    surfaceKeys: ['fairway', 'firstCut', 'secondCut', 'cartPath'],
    features: ['markers150'],
  },
  {
    id: 'hazards',
    label: 'Hazards',
    surfaceKeys: ['outOfBounds', 'water', 'bunker', 'waste', 'hazard'],
    features: ['trees', 'houses'],
  },
];

/** Surface keys that belong to a layer (fringe is drawn from green; not a layer). */
const surfaceToLayer = new Map<SurfaceKey, LayerId>(
  LAYER_CONFIG.flatMap((c) => c.surfaceKeys.map((k) => [k, c.id] as [SurfaceKey, LayerId]))
);

const featureToLayer = new Map<LayerFeatureKey, LayerId>(
  LAYER_CONFIG.flatMap((c) => c.features.map((f) => [f, c.id] as [LayerFeatureKey, LayerId]))
);

export function getLayerForSurfaceKey(key: SurfaceKey): LayerId | null {
  return surfaceToLayer.get(key) ?? null;
}

export function getLayerForFeature(feature: LayerFeatureKey): LayerId | null {
  return featureToLayer.get(feature) ?? null;
}

export type LayerVisibility = Record<LayerId, boolean>;

export function getDefaultLayerVisibility(): LayerVisibility {
  const vis: LayerVisibility = {} as LayerVisibility;
  for (const id of LAYER_IDS) {
    vis[id] = true;
  }
  return vis;
}
